import { WIEDERVORLAGE_VORGABEN, vorgabeZuTag, type Pinziel } from '@netcon-work/shared'
import { useEffect, useId, useRef, useState } from 'react'
import { useMerken } from '../lib/mutationen'
import { Button } from './ui/primitives'

/**
 * **Notiz und Wiedervorlage zu einem Pin** ([Z-03](../../../../docs/phasen/zusatz-03-auf-dem-schirm.md),
 * [E-216](../../../../docs/ENTSCHEIDUNGEN.md#e-216)).
 *
 * ## Warum das Feld an zwei Stellen steht
 *
 * Zuerst gab es nur eine: die bereits gepinnte Zeile in „Mein Tag". Das folgte der Regel
 * *„Anpinnen bleibt ein Klick"* – und war trotzdem falsch. Nils, 24.08.2026, nach dem ersten
 * Ausprobieren: *„sehe wenn ich die PIN drücke kein Datumsfeld oder so."*
 *
 * Er hat recht, und es ist derselbe Fehler, den [Z-02.11](../../../../docs/phasen/zusatz-02-mein-tag.md)
 * beim Tagesziel schon einmal vermieden hat: **ein Feld, das man an der Stelle der Absicht nicht
 * findet, gibt es nicht.** Wer eine Wiedervorlage setzen will, denkt in dem Moment daran, in dem
 * er anpinnt – nicht eine Ansicht später.
 *
 * Die ursprüngliche Regel bleibt trotzdem gültig: **das Pinnen selbst geschieht sofort**, ohne
 * Rückfrage. Das Feld geht *danach* auf und lässt sich wegklicken; wer nur pinnen wollte, hat
 * schon gepinnt und ignoriert es. Ein Dialog **vor** dem Pinnen wäre die Formularübung, die
 * niemand will – einer danach ist ein Angebot.
 *
 * ## Vorgaben statt Kalender
 *
 * Für „nächste Woche" einen Datumswähler zu öffnen ist drei Klicks zu viel. Das freie Feld
 * bleibt daneben stehen, für den seltenen Fall eines echten Termins.
 */
export function Merkfeld({
  ziel,
  notiz,
  wiedervorlage,
  schliessen,
  className = '',
}: {
  ziel: Pinziel
  notiz: string | null
  wiedervorlage: string | null
  schliessen: () => void
  className?: string
}) {
  const merken = useMerken()
  const [text, setText] = useState(notiz ?? '')
  const [tag, setTag] = useState(wiedervorlage ?? '')
  const feldId = useId()
  const eingabe = useRef<HTMLInputElement>(null)

  /**
   * **Der Zeiger steht im Notizfeld.** Es ist das einzige Pflichtfeld, sobald ein Datum kommt –
   * und wer erst klicken muss, um schreiben zu können, schreibt nichts.
   */
  useEffect(() => {
    eingabe.current?.focus()
  }, [])

  /**
   * **Die Notiz ist Pflicht, sobald ein Datum gesetzt ist** – hier nur als Hinweis. Erzwungen
   * wird sie in Zod auf dem Server; eine Regel, die nur im Formular steht, fehlt beim zweiten
   * Aufrufer ([E-216](../../../../docs/ENTSCHEIDUNGEN.md#e-216)).
   */
  const fehltNotiz = tag !== '' && text.trim() === ''

  const speichern = () => {
    if (fehltNotiz) return
    merken.mutate({
      ziel,
      notiz: text.trim() === '' ? null : text.trim(),
      wiedervorlage: tag === '' ? null : tag,
    })
    schliessen()
  }

  return (
    <div className={`rounded-lg border border-line2 bg-page p-2.5 ${className}`}>
      <label className="block text-[11px] font-semibold text-ink2" htmlFor={feldId}>
        Warum liegt das hier?
      </label>
      <input
        id={feldId}
        ref={eingabe}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') speichern()
          if (e.key === 'Escape') schliessen()
        }}
        maxLength={200}
        placeholder={'z. B. „vor Quartalsende mit Meyer klären"'}
        className="mt-1 w-full rounded-md border border-line2 bg-surface px-2 py-1 text-[12.5px]"
      />

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-muted">Wieder vorlegen:</span>
        {WIEDERVORLAGE_VORGABEN.map((v) => {
          const wert = vorgabeZuTag(v.tage, new Date())
          return (
            <button
              key={v.schluessel}
              type="button"
              aria-pressed={tag === wert}
              onClick={() => setTag(wert)}
              className={`cursor-pointer rounded-lg border px-2 py-[2px] text-[11px] ${
                tag === wert
                  ? 'border-accent bg-accent-soft text-accent-deep'
                  : 'border-line2 bg-surface text-ink2 hover:bg-page'
              }`}
            >
              {v.label}
            </button>
          )
        })}
        <input
          type="date"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          className="rounded-md border border-line2 bg-surface px-1.5 py-[2px] text-[11px]"
          aria-label="Datum der Wiedervorlage"
        />
        {tag !== '' && (
          <button
            type="button"
            onClick={() => setTag('')}
            className="cursor-pointer text-[11px] text-muted underline"
          >
            ohne Datum
          </button>
        )}
      </div>

      {fehltNotiz && (
        <p className="mt-1.5 text-[11px] text-warn">
          Zu einer Wiedervorlage gehört eine Notiz – ohne sie ist der Pin beim Aufwachen ein Rätsel.
        </p>
      )}

      <div className="mt-2 flex items-center gap-2">
        <Button size="sm" variant="primary" onClick={speichern} disabled={fehltNotiz}>
          Merken
        </Button>
        <Button size="sm" variant="ghost" onClick={schliessen}>
          Abbrechen
        </Button>
        {/*
          Die Beruhigung, ohne die das Feld wie eine Pflicht aussieht: gepinnt ist schon,
          der Rest ist freiwillig.
        */}
        <span className="text-[11px] text-muted">Angepinnt ist sie bereits.</span>
      </div>
    </div>
  )
}
